/**
 * useFormKeyboard — Enter/Escape/Arrow keyboard navigation for forms.
 *
 * Provides a field registry and navigation methods so pressing Enter advances
 * to the next field, Escape goes back, and ArrowLeft/Right navigate within
 * a line-item row (number inputs only).
 *
 * Usage:
 *   const { field, advance, focusField } = useFormKeyboard({ onSubmit, onEscape })
 *
 *   // In template:
 *   <IntercomInput ref="qtyRef" @keydown.enter.prevent="advance('qty')" />
 *   <IntercomSelect @change="advance('customer')" />
 *
 *   // In setup:
 *   field('customer', customerRef)
 *   field('qty', qtyRef, { row: 'item-0' })
 *   field('price', priceRef, { row: 'item-0' })
 */

import { ref, nextTick } from 'vue'

/**
 * @param {Object} options
 * @param {Function} [options.onSubmit] - Called when advancing past the last field
 * @param {Function} [options.onEscape] - Called when Escape is pressed with nothing to close
 */
export function useFormKeyboard(options = {}) {
  // Ordered list of field definitions
  const fields = ref([])

  /**
   * Register a field in the navigation chain.
   * Call in order — fields are navigated in registration order.
   *
   * @param {string} name - Unique field name (e.g., 'customer', 'item-0-qty')
   * @param {import('vue').Ref} fieldRef - Template ref to the component/element
   * @param {Object} [opts] - Options
   * @param {string} [opts.row] - Row group for ArrowLeft/Right navigation (e.g., 'item-0')
   * @param {string} [opts.type] - 'select' | 'textarea' | 'number' | 'text' | 'date' (default: 'text')
   */
  function field(name, fieldRef, opts = {}) {
    // Remove existing registration with same name (for dynamic re-registration)
    const existingIdx = fields.value.findIndex((f) => f.name === name)
    if (existingIdx >= 0) {
      fields.value[existingIdx] = { name, ref: fieldRef, ...opts }
    } else {
      fields.value.push({ name, ref: fieldRef, ...opts })
    }
  }

  /**
   * Remove a field from the registry (for dynamic items being removed).
   * @param {string} name
   */
  function unregisterField(name) {
    fields.value = fields.value.filter((f) => f.name !== name)
  }

  /**
   * Clear all fields and re-register (for when line items change).
   */
  function clearFields() {
    fields.value = []
  }

  /**
   * Focus the first registered field. Call this after form data has loaded.
   * Retries until the field becomes focusable (handles async-disabled fields).
   * @param {number} [maxRetries=15] - Max retry attempts (100ms apart)
   */
  async function focusFirst(maxRetries = 15) {
    const attempt = async (n) => {
      await nextTick()
      for (const entry of fields.value) {
        if (!entry.ref?.value) continue
        const focused = await focusField(entry.name)
        if (focused) return
      }
      // First field may be disabled (loading) — retry
      if (n < maxRetries) {
        setTimeout(() => attempt(n + 1), 100)
      }
    }
    attempt(0)
  }

  /**
   * Focus a field by name.
   * @param {string} name
   */
  async function focusField(name) {
    await nextTick()
    const entry = fields.value.find((f) => f.name === name)
    if (!entry || !entry.ref?.value) return false

    const component = entry.ref.value

    // Try component's .focus() method first (defineExpose)
    if (typeof component.focus === 'function') {
      component.focus()
      // Verify focus actually landed — disabled elements silently reject focus
      await nextTick()
      const root = component.$el || component
      const active = document.activeElement
      if (active && active !== document.body && (root === active || root.contains?.(active))) {
        return true
      }
      // focus() was called but didn't land (element likely disabled) — fall through
    }

    // Fallback: find the first enabled native element inside the component
    const el = component.$el || component
    const input = el?.querySelector?.('input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])')
    if (input) {
      input.focus()
      if (input.type === 'number' || input.type === 'text') {
        input.select?.()
      }
      return document.activeElement === input
    }
    return false
  }

  /**
   * Advance from the current field to the next.
   * For selects, call this from @change. For inputs, from @keydown.enter.prevent.
   *
   * @param {string} currentName - Name of the field the user just completed
   */
  async function advance(currentName) {
    const idx = fields.value.findIndex((f) => f.name === currentName)
    if (idx < 0) return

    // Find next non-textarea field (textareas use Tab, not Enter)
    for (let i = idx + 1; i < fields.value.length; i++) {
      const next = fields.value[i]
      // Skip fields whose ref is null (conditionally hidden)
      if (!next.ref?.value) continue
      // Skip textareas — Enter inserts newlines there
      if (next.type === 'textarea') continue
      const focused = await focusField(next.name)
      if (focused) return
    }

    // Past the last field — trigger submit
    if (options.onSubmit) {
      options.onSubmit()
    }
  }

  /**
   * Retreat (go back) from the current field to the previous.
   * Call from Escape key handler.
   *
   * @param {string} currentName - Name of the current field
   */
  async function retreat(currentName) {
    const idx = fields.value.findIndex((f) => f.name === currentName)

    // Find previous non-textarea field
    for (let i = (idx >= 0 ? idx : fields.value.length) - 1; i >= 0; i--) {
      const prev = fields.value[i]
      if (!prev.ref?.value) continue
      if (prev.type === 'textarea') continue
      const focused = await focusField(prev.name)
      if (focused) return
    }

    // No previous field — trigger escape callback
    if (options.onEscape) {
      options.onEscape()
    }
  }

  /**
   * Move to the previous field in the same row (ArrowLeft on number inputs).
   * Only works when cursor is at position 0.
   *
   * @param {string} currentName
   * @param {KeyboardEvent} event - The keydown event (to check cursor position)
   */
  async function moveLeft(currentName, event) {
    const input = event.target
    // Only advance when cursor is at the start of the value
    if (input.selectionStart !== 0) return

    const entry = fields.value.find((f) => f.name === currentName)
    if (!entry?.row) return

    const idx = fields.value.findIndex((f) => f.name === currentName)
    // Find previous field in the same row
    for (let i = idx - 1; i >= 0; i--) {
      const prev = fields.value[i]
      if (prev.row !== entry.row) break // left the row
      if (!prev.ref?.value) continue
      event.preventDefault()
      await focusField(prev.name)
      return
    }
  }

  /**
   * Move to the next field in the same row (ArrowRight on number inputs).
   * Only works when cursor is at the end of the value.
   *
   * @param {string} currentName
   * @param {KeyboardEvent} event
   */
  async function moveRight(currentName, event) {
    const input = event.target
    // Only advance when cursor is at the end of the value
    if (input.selectionStart !== input.value?.length) return

    const entry = fields.value.find((f) => f.name === currentName)
    if (!entry?.row) return

    const idx = fields.value.findIndex((f) => f.name === currentName)
    // Find next field in the same row
    for (let i = idx + 1; i < fields.value.length; i++) {
      const next = fields.value[i]
      if (next.row !== entry.row) break // left the row
      if (!next.ref?.value) continue
      event.preventDefault()
      await focusField(next.name)
      return
    }
  }

  /**
   * Handle Escape: close select → blur field → call onEscape.
   * Call from a root @keydown.escape handler.
   *
   * @param {KeyboardEvent} event
   */
  function handleEscape(event) {
    // If an IntercomSelect dropdown is open, let it handle Escape first
    if (event.defaultPrevented) return

    // If a dropdown is open in the DOM, don't process
    const openDropdown = document.querySelector(
      '.intercom-select-container--open, .intercom-select-dropdown'
    )
    if (openDropdown) return

    // Blur the active element first
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur()
      event.preventDefault()
      return
    }

    // Nothing focused — call the escape handler
    if (options.onEscape) {
      event.preventDefault()
      options.onEscape()
    }
  }

  return {
    field,
    fields,
    unregisterField,
    clearFields,
    focusFirst,
    focusField,
    advance,
    retreat,
    moveLeft,
    moveRight,
    handleEscape,
  }
}
